import React from 'react';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { useTheme } from 'styled-components';

import {
  CURRENCIES,
  HANDLING_MODES,
  HANDLING_UNIQUE_NAME,
  media,
} from '@savgroup-front-common/constants';
import { getCountryTranslationSelector } from '@savgroup-front-common/core/domains/i18n/selectors';
import { formatSimpleAddressOneLine } from '@savgroup-front-common/core/formatters/address';
import { getHandlingCardLabel } from '@savgroup-front-common/core/formatters/getHandlingCardLabel/getHandlingCardLabel';
import { HandlingSummary } from '@savgroup-front-common/types';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import { ClaimGroupConfirmation } from '@/api/Claim/getClaimGroupConfirmationQuery';
import {
  SimpleItemCard,
  SimpleItemCardMobile,
} from '@/view/atoms/SimpleItemCard';

import { getHandlingData } from '../../../helpers';

import { adaptDepositCardAddress } from './adapters/adaptDepositCardAddress';
import messages from './messages';

interface ResumeDepositProps {
  claimGroupConfirmation?: ClaimGroupConfirmation;
  handlingStatus?: HANDLING_MODES;
  handlingSummary?: HandlingSummary;
}

const ResumeDeposit: React.FC<ResumeDepositProps> = ({
  claimGroupConfirmation,
  handlingStatus,
  handlingSummary,
}) => {
  const isTransportPlanFeatureEnabled = !!handlingSummary;

  const isMobileView = useMedia(media.maxWidth.xs);
  const theme = useTheme();
  const getCountryTranslation = useSelector(getCountryTranslationSelector);

  const fallbackAddressStreet = formatSimpleAddressOneLine(
    claimGroupConfirmation?.depositAddress,
    getCountryTranslation,
  );

  const { label, description } = getHandlingData({
    carrierUniqueName:
      claimGroupConfirmation?.carrierDepositTransportMethodUniqueName as HANDLING_UNIQUE_NAME,
  });
  const { label: newLabel, description: newDescription } = getHandlingCardLabel(
    {
      transportMethodType: handlingSummary?.deposit?.transportPlanMethodType,
    },
  );

  const deposit = handlingSummary?.deposit;
  const prefixTitle =
    handlingStatus === HANDLING_MODES.HOME
      ? messages.depositTitleIsHome
      : messages.depositTitleIsDeposit;

  if (isTransportPlanFeatureEnabled && deposit) {
    const address = adaptDepositCardAddress({
      deposit,
      fallbackAddressStreet,
      getCountryTranslation,
    });

    return isMobileView ? (
      <SimpleItemCardMobile
        icon={ICONS_TYPE.DEPOSIT_ICON}
        iconColor={theme.colors.primary}
        prefixTitle={prefixTitle}
        title={newLabel}
        didactic={newDescription}
        address={address}
        price={{
          priceValue: deposit.transportPlanCostWithTax?.amount,
          priceCurrencyCode: deposit.transportPlanCostWithTax?.currency,
        }}
        isDeductibleFromRefund={deposit.isDeductible}
      />
    ) : (
      <SimpleItemCard
        icon={ICONS_TYPE.DEPOSIT_ICON}
        iconColor={theme.colors.primary}
        prefixTitle={prefixTitle}
        title={newLabel}
        didactic={newDescription}
        address={address}
        price={{
          priceValue: deposit.transportPlanCostWithTax?.amount,
          priceCurrencyCode: deposit.transportPlanCostWithTax?.currency,
        }}
        isDeductibleFromRefund={deposit.isDeductible}
      />
    );
  }

  return isMobileView ? (
    <SimpleItemCardMobile
      icon={ICONS_TYPE.DEPOSIT_ICON}
      iconColor={theme.colors.primary}
      prefixTitle={prefixTitle}
      title={label}
      didactic={description}
      address={{
        label: messages.sendToCompany,
        addressStreet: fallbackAddressStreet,
      }}
      price={{
        priceValue: claimGroupConfirmation?.carrierDepositPriceWithTax,
        priceCurrencyCode:
          claimGroupConfirmation?.carrierDepositPriceWithTaxCurrencyCode as CURRENCIES,
      }}
    />
  ) : (
    <SimpleItemCard
      icon={ICONS_TYPE.DEPOSIT_ICON}
      iconColor={theme.colors.primary}
      prefixTitle={prefixTitle}
      title={label}
      didactic={description}
      address={{
        label: messages.sendToCompany,
        addressStreet: fallbackAddressStreet,
      }}
      price={{
        priceValue: claimGroupConfirmation?.carrierDepositPriceWithTax,
        priceCurrencyCode:
          claimGroupConfirmation?.carrierDepositPriceWithTaxCurrencyCode as CURRENCIES,
      }}
    />
  );
};

export default ResumeDeposit;
